/**
 * Offline Existing-Asset Queue Indicator (v1.1.26)
 * ================================================
 * Small chip that surfaces the count of pending operations in the v1.1.25
 * existing-asset offline queue (separate from the existing offline banner
 * which surfaces NEW-asset queue status).
 *
 * DESIGN INVARIANT (per product owner directive 2026-02-06):
 *   This component is entirely additive. It does not modify, replace, or
 *   interfere with the existing OfflineContext banner, OfflineRecovery
 *   component, or any other offline UI. It only reads from the NEW
 *   queue's count function (getPendingExistingAssetCount) that was
 *   shipped in v1.1.25.
 *
 *   If this file is deleted, nothing else changes — including the v1.1.25
 *   offline-existing-asset flow itself, which keeps queueing and draining
 *   normally without any user-facing surface.
 *
 * Behaviour:
 *   - Chip is HIDDEN when count === 0 AND no sync in progress (idle).
 *   - Chip shows AMBER with "N pending" when count > 0.
 *   - Chip shows TEAL with spinner "Syncing N…" while a drain is in flight.
 *   - Tappable: invokes drainExistingAssetQueue() for a manual "sync now".
 *
 * Polling strategy:
 *   - Polls the count every 3s while mounted (cheap — IndexedDB getAll).
 *   - Subscribes to the sync worker's events for instant updates without
 *     waiting for the next poll cycle.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CloudUpload, Loader2 } from 'lucide-react';
import {
  drainExistingAssetQueue,
  subscribeExistingAssetSync,
} from '../services/offlineExistingAssetSync';
import { getPendingExistingAssetCount } from '../services/offlineExistingAssetQueue';

const POLL_MS = 3000;

export const OfflineExistingAssetIndicator = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState('idle');
  // 'idle' | 'syncing'
  const [syncProgress, setSyncProgress] = useState(null);

  const refreshCount = useCallback(async () => {
    try {
      const n = await getPendingExistingAssetCount();
      setPendingCount(n);
    } catch {
      // Non-fatal — if the count read fails we just hide the chip.
      setPendingCount(0);
    }
  }, []);

  // Poll loop
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await refreshCount();
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refreshCount]);

  // Subscribe to sync worker for instant updates
  useEffect(() => {
    const unsub = subscribeExistingAssetSync(evt => {
      if (evt.type === 'started') {
        setSyncStatus('syncing');
        setSyncProgress({ done: 0, total: evt.total });
      } else if (evt.type === 'progress') {
        setSyncProgress({
          done: (evt.succeeded || 0) + (evt.failed || 0),
          total: evt.total,
        });
      } else if (evt.type === 'finished') {
        setSyncStatus('idle');
        setSyncProgress(null);
        // Refresh count immediately so the chip hides as soon as queue drains.
        refreshCount();
      }
    });
    return unsub;
  }, [refreshCount]);

  const handleManualSync = useCallback(() => {
    if (!navigator.onLine) return;
    drainExistingAssetQueue();
  }, []);

  // Idle state with empty queue → render nothing.
  if (syncStatus === 'idle' && pendingCount === 0) {
    return null;
  }

  if (syncStatus === 'syncing') {
    const done = syncProgress?.done ?? 0;
    const total = syncProgress?.total ?? pendingCount;
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200"
        data-testid="offline-existing-asset-syncing-chip"
        title="Syncing queued photos + re-analyses for preloaded assets"
      >
        <Loader2 size={12} className="animate-spin" />
        <span>Syncing {done}/{total}…</span>
      </div>
    );
  }

  // pendingCount > 0
  const tappable = navigator.onLine;
  return (
    <button
      type="button"
      onClick={tappable ? handleManualSync : undefined}
      className={
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ' +
        (tappable
          ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 cursor-pointer'
          : 'bg-amber-50 text-amber-700 border-amber-200 opacity-90 cursor-default')
      }
      data-testid="offline-existing-asset-pending-chip"
      title={tappable
        ? `${pendingCount} preloaded-asset update${pendingCount > 1 ? 's' : ''} queued. Click to sync now.`
        : `${pendingCount} preloaded-asset update${pendingCount > 1 ? 's' : ''} queued. Will sync when back online.`
      }
    >
      <CloudUpload size={12} />
      <span>{pendingCount} pending</span>
    </button>
  );
};

export default OfflineExistingAssetIndicator;
